import type { HistoryEntry, HistorySource, ParameterTarget } from '../../services/measurementService';
import type { BadgeTone } from '../atoms/Badge/Badge.types';
import type { MeasurementStatus } from '../../hooks/useLatestMeasurements';

/** Parameter ids that have a translated name (measurement.history.parameterNames.<id>). */
export const KNOWN_PARAMETER_IDS = [
  'alkalinity',
  'calcium',
  'magnesium',
  'nitrate',
  'phosphate',
  'salinity',
  'temperature',
  'ph',
  'potassium',
  'iodine',
  'strontium',
] as const;

const KNOWN = new Set<string>(KNOWN_PARAMETER_IDS);

type TFunction = (key: string, options?: Record<string, unknown>) => string;

/**
 * ICP parameter ids with a name in measurement.history.parameterNames that are not
 * hand-test parameters: physico-chemical values and nitrite/ammonia.
 */
const OTHER_NAMED_IDS: ReadonlySet<string> = new Set([
  'nitrite', 'ammonia', 'ec', 'density', 'rel_density', 'co2', 'sbv', 'alkalinity_mmol',
  'sio2', 'sulfate', 'sak254', 'npoc', 'tnb',
]);

/** ICP element symbols (and total phosphate) stored as parameter ids, named in icp.parameterNames. */
const ICP_NAMED_IDS: ReadonlySet<string> = new Set([
  'po4_total', 'Na', 'Mg', 'K', 'Ca', 'Sr', 'B', 'Li', 'Ba', 'S', 'P', 'F', 'Cl', 'Br', 'I', 'Si', 'Mo',
  'Ni', 'Mn', 'Fe', 'Zn', 'Cu', 'Cr', 'Co', 'V', 'Se', 'Sn', 'Al', 'Ti', 'Sc', 'Rb', 'Zr', 'Nb', 'Hf', 'W',
  'Pb', 'As', 'Cd', 'Hg', 'Sb', 'Tl', 'Be', 'Bi', 'Ag', 'Ga', 'Ge', 'Te', 'La', 'Ce', 'Nd', 'Cs', 'Th', 'U',
]);

/**
 * Translated parameter name. ICP values used to show the lab's English names in
 * every language ("Antimony", "Acid binding capacity" — audit §3.6); unknown ids
 * keep the stored name.
 */
export function parameterLabel(t: TFunction, parameterId: string, fallbackName: string): string {
  if (KNOWN.has(parameterId) || OTHER_NAMED_IDS.has(parameterId)) {
    return t(`measurement.history.parameterNames.${parameterId}`);
  }
  return ICP_NAMED_IDS.has(parameterId) ? t(`icp.parameterNames.${parameterId}`) : fallbackName;
}

export function sourceLabelKey(source: HistorySource): string {
  return `measurement.history.source.${source}`;
}

export interface ParameterSeries {
  readonly parameterId: string;
  readonly parameterName: string;
  readonly unit: string;
  /** Oldest first (chart order). */
  readonly points: readonly HistoryEntry[];
}

/**
 * Groups history entries per parameter. Parameters are ordered by their
 * number of values (most first), then name, so the default selection is the
 * parameter the user tests most.
 */
export function groupByParameter(entries: readonly HistoryEntry[]): ParameterSeries[] {
  const groups = new Map<string, HistoryEntry[]>();
  for (const entry of entries) {
    const list = groups.get(entry.parameterId);
    if (list) list.push(entry);
    else groups.set(entry.parameterId, [entry]);
  }
  const series: ParameterSeries[] = [];
  for (const [parameterId, list] of groups) {
    const points = [...list].sort((a, b) => Date.parse(a.measuredAt) - Date.parse(b.measuredAt));
    const latest = points[points.length - 1];
    series.push({
      parameterId,
      parameterName: latest?.parameterName ?? parameterId,
      unit: latest?.unit ?? '',
      points,
    });
  }
  return series.sort((a, b) => b.points.length - a.points.length || a.parameterName.localeCompare(b.parameterName));
}

/**
 * Translation key of a parameter status (deriveStatus) — the one vocabulary the
 * dashboard tiles, the latest-values table and the history table share, so a value
 * is never "acceptable" on one screen and "out of range" on another (audit §3.3).
 */
export function parameterStatusKey(status: MeasurementStatus): string | null {
  return status === 'unknown' ? null : `dashboard.metricStatus.${status}`;
}

const STATUS_TONES: Readonly<Record<Exclude<MeasurementStatus, 'unknown'>, BadgeTone>> = {
  excellent: 'success',
  stable: 'warning',
  warning: 'warning',
  critical: 'danger',
};

/** Badge tone of a parameter status; the label always carries the meaning too. */
export function parameterStatusTone(status: MeasurementStatus): BadgeTone {
  return status === 'unknown' ? 'neutral' : STATUS_TONES[status];
}

export function isInTarget(value: number, target: ParameterTarget | undefined): boolean | null {
  if (!target) return null;
  return value >= target.min && value <= target.max;
}

export interface SeriesSummary {
  readonly count: number;
  readonly latest: HistoryEntry | null;
  readonly min: number | null;
  readonly max: number | null;
  /** Values inside the target range, or null when the parameter has no target. */
  readonly inTarget: number | null;
}

export function summarizeSeries(points: readonly HistoryEntry[], target: ParameterTarget | undefined): SeriesSummary {
  if (points.length === 0) return { count: 0, latest: null, min: null, max: null, inTarget: target ? 0 : null };
  const values = points.map((p) => p.value);
  return {
    count: points.length,
    latest: points[points.length - 1] ?? null,
    min: Math.min(...values),
    max: Math.max(...values),
    inTarget: target ? points.filter((p) => isInTarget(p.value, target)).length : null,
  };
}

export function formatNumber(value: number, language: string): string {
  return new Intl.NumberFormat(language, { maximumFractionDigits: 3 }).format(value);
}

export function formatDate(iso: string | number, language: string): string {
  return new Intl.DateTimeFormat(language, { dateStyle: 'medium' }).format(new Date(iso));
}

/** `YYYY-MM-DDTHH:mm` in local time, for an Input type="datetime-local". */
export function toDateTimeLocalValue(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
